/* 4PIXELS — shared header compatibility guard.
   Product header transparency/geometry/state is owned ONLY by header.liquid V9.
   This file must not move or paint product content. */
(() => {
  'use strict';
  if (window.__fourPixelsHeaderGeometryGuard) return;
  window.__fourPixelsHeaderGeometryGuard = true;

  const root = document.documentElement;
  const RTL_LANG = /^(ar|he|fa|ur)(-|$)/i;
  let raf = 0;
  let resizeObserver;

  const shell = () => document.querySelector('[data-fp-header-shell]');
  const section = () => document.getElementById('shopify-section-header') || shell()?.closest('[id^="shopify-section-"]') || null;
  const main = () => document.getElementById('MainContent');

  function direction() {
    const dir = String(root.getAttribute('dir') || '').toLowerCase();
    if (dir === 'rtl' || dir === 'ltr') return dir;
    return RTL_LANG.test(String(root.getAttribute('lang') || '')) ? 'rtl' : 'ltr';
  }

  function enforceDirection(el) {
    if (!el) return;
    const dir = direction();
    el.setAttribute('dir', dir);
    el.setAttribute('data-fp-direction-lock', dir);

    const cart = el.querySelector('mini-cart[data-fp-cart-panel], #mini-cart');
    if (!cart) return;
    cart.setAttribute('dir', dir);
    cart.setAttribute('data-fp-cart-dir', dir);
    cart.style.setProperty('left', dir === 'rtl' ? '0' : 'auto', 'important');
    cart.style.setProperty('right', dir === 'rtl' ? 'auto' : '0', 'important');
    cart.style.setProperty('margin-left', dir === 'rtl' ? '0' : 'auto', 'important');
    cart.style.setProperty('margin-right', dir === 'rtl' ? 'auto' : '0', 'important');
    cart.style.setProperty('transform-origin', dir === 'rtl' ? 'left center' : 'right center', 'important');
  }

  function arabicHomeOverlap(wrap, height) {
    if (direction() !== 'rtl' || !document.body?.classList.contains('template-index')) return;
    const overlap = `-${height}px`;
    wrap.style.setProperty('margin-bottom', overlap, 'important');
    wrap.style.setProperty('margin-block-end', overlap, 'important');
    const content = main();
    if (content) {
      content.style.setProperty('margin-top', '0px', 'important');
      content.style.setProperty('padding-top', '0px', 'important');
    }
  }

  function sync() {
    raf = 0;
    const el = shell();
    const wrap = section();
    if (!el || !wrap) return;

    /* Measure before writing direction/cart styles to avoid a forced layout flush. */
    const height = Math.round(el.getBoundingClientRect().height);
    if (height <= 0) return;

    enforceDirection(el);
    root.style.setProperty('--clozs-header-height', `${height}px`);

    /* Product pages are deliberately untouched here. V9 owns their overlap,
       transparency and scroll/panel state for both LTR and RTL. */
    if (!document.body?.classList.contains('template-product')) {
      arabicHomeOverlap(wrap, height);
    }
  }

  function queue() {
    if (raf) return;
    raf = requestAnimationFrame(sync);
  }

  function observe() {
    const el = shell();
    if (!el) return;

    resizeObserver?.disconnect();
    if ('ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(queue);
      resizeObserver.observe(el);
    }
  }

  function init() {
    sync();
    observe();
    document.fonts?.ready?.then(queue).catch(() => {});
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();

  window.addEventListener('resize', queue, { passive: true });
  window.addEventListener('pageshow', queue);
  document.addEventListener('cartdrawer:opened', queue);
  document.addEventListener('shopify:section:load', () => { queue(); observe(); });
})();

/* 4PIXELS — NEW CUSTOMER ACCOUNTS MOBILE MENU FIX
   Shopify New Customer Accounts can be active while the legacy Liquid flag
   shop.customer_accounts_enabled is false. Keep a visible account entry above
   Saved items / Language in the mobile drawer. */
(() => {
  'use strict';
  if (window.__fourPixelsMobileAccountFix) return;
  window.__fourPixelsMobileAccountFix = true;

  const isArabic = () => {
    const html = document.documentElement;
    return String(html.dir || '').toLowerCase() === 'rtl' || /^ar(?:-|$)/i.test(String(html.lang || ''));
  };

  function ensureAccountLink() {
    const header = document.querySelector('[data-fp-header-shell]');
    if (!header) return;

    const utilities = header.querySelector('.fp-mobile-drawer__utilities');
    const quickRow = utilities && utilities.querySelector('.fp-mobile-drawer__quick-row');
    if (!utilities || !quickRow) return;

    const existing = utilities.querySelector('.fp-mobile-utility--account, [data-fp-mobile-account-link]');
    if (existing) {
      const label = existing.querySelector('span');
      if (label) label.textContent = isArabic() ? 'حسابي' : 'Account';
      return;
    }

    const sectionId = header.getAttribute('data-fp-header-instance') || '';
    const link = document.createElement('a');
    link.href = '/account';
    link.className = 'fp-mobile-utility fp-mobile-utility--account';
    link.setAttribute('data-fp-mobile-account-link', 'true');
    link.setAttribute('aria-label', isArabic() ? 'حسابي' : 'Account');

    const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    icon.setAttribute('viewBox', '0 0 24 24');
    icon.setAttribute('aria-hidden', 'true');
    icon.setAttribute('focusable', 'false');
    icon.innerHTML = sectionId
      ? '<use href="#fp-account-' + sectionId + '"></use>'
      : '<circle cx="12" cy="8" r="3.5"></circle><path d="M5.3 20c.7-4 3.1-6 6.7-6s6 2 6.7 6"></path>';

    const label = document.createElement('span');
    label.textContent = isArabic() ? 'حسابي' : 'Account';

    link.appendChild(icon);
    link.appendChild(label);
    utilities.insertBefore(link, quickRow);
  }

  function init() {
    ensureAccountLink();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  window.addEventListener('pageshow', ensureAccountLink);
  document.addEventListener('shopify:section:load', ensureAccountLink);
})();
